import { useTranslation } from 'react-i18next';
import CustomCarousel, { Item } from '../CustomCarousel';
import useScreenType from '../../../hooks/utility/useScreenType';
import classnames from 'classnames';
import React, { ReactElement, useMemo } from 'react';
import useMobileApp from '../../../hooks/mobile-app/useMobileApp';
import useDealsItems, { DealItem } from './useDealsItems';
import DiscountCard, { Variant } from '../../discount/DiscountCard';

interface Props {
  className?: string;
  itemsClassName?: string;
  dealClassName?: string;
}

// Only `width` is consumed by the carousel; the card owns its height (carousels run `autoHeight`).
// Text panel is min-h-[72px] (py-2.5×2 + 2-line title + gap + description); it grows to fit the
// discount-card status tag when one is shown.
const IMAGE_CARD = {
  desktop: { width: 400, height: 280 },
  mobile: { width: 300, height: 224 },
};

const toCarouselItems = (items: DealItem[], renderCard: (item: DealItem, idx: number) => ReactElement): Item[] =>
  items.map((item, idx) => ({
    data: item,
    render: () => renderCard(item, idx),
  }));

const DealsCarousel = {
  Classic: null as any,
  Image: null as any,
};

export default DealsCarousel;

const Classic = ({ className, itemsClassName, dealClassName }: Props): ReactElement | null => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Carousel:DealsCarousel' });
  const { items } = useDealsItems(dealClassName);
  const { isDesktop, isTablet } = useScreenType();
  const { isMobileApp } = useMobileApp();

  const itemsPerSlide = useMemo(() => {
    if (isDesktop) return 2.5;

    if (isTablet) return 2.1;

    return 1.1;
  }, [isDesktop, isTablet]);

  if (!items.length) return null;

  const carouselItems = toCarouselItems(items, (item, idx) => (
    <DiscountCard {...item} key={item.key} variant={((idx % 5) + 1) as Variant} image={undefined} />
  ));

  return (
    <div className={classnames('relative w-full', className)}>
      <CustomCarousel
        itemWidth={isDesktop ? 400 : undefined}
        items={carouselItems}
        itemsPerSlide={itemsPerSlide}
        slideJump={isDesktop ? 3 : 2}
        autoHeight
        arrowsPosition="top"
        arrowsClassName={classnames('lg:mr-lg mr-md', { 'mb-sm': isMobileApp })}
        itemsClassName={classnames('lg:pl-none pl-md', itemsClassName)}
        title={!isMobileApp && <h5 className="lg:ml-none ml-md font-semibold">{t('title')}</h5>}
      />
    </div>
  );
};

const Image = ({ className, itemsClassName, dealClassName }: Props): ReactElement | null => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Carousel:DealsCarousel' });
  const { items } = useDealsItems(dealClassName);
  const { isDesktop } = useScreenType();
  const { isMobileApp } = useMobileApp();

  if (!items.length) return null;

  const title = isMobileApp && <h5 className="lg:ml-none ml-md font-semibold">{t('title')}</h5>;

  const cardSize = isDesktop ? IMAGE_CARD.desktop : IMAGE_CARD.mobile;

  const carouselItems = toCarouselItems(items, (item, idx) => (
    <DiscountCard {...item} key={item.key} variant={((idx % 5) + 1) as Variant} />
  ));

  return (
    <div className={classnames('relative w-full', className)}>
      <CustomCarousel
        items={carouselItems}
        itemWidth={cardSize.width}
        // Desktop content is at most 1200px wide, so only 2 image cards (2×412px) always fit; a 3rd is clipped.
        itemsPerSlide={isDesktop ? 2 : 1}
        autoHeight
        slideJump={isDesktop ? 3 : 1}
        hideArrows={!isDesktop}
        arrowsPosition="top"
        arrowsClassName="lg:mr-lg mr-md mb-sm"
        itemsClassName={classnames('lg:pl-none pl-md', itemsClassName)}
        title={title}
      />
    </div>
  );
};

DealsCarousel.Classic = Classic;
DealsCarousel.Image = Image;
